/**
 * API endpoint configuration for the Auction Chase platform.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3200';
const WS_BASE_URL = process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:3200';

export const API_ENDPOINTS = {
  API_BASE_URL,
  WS_BASE_URL,
  AUTH: {
    LOGIN: `${API_BASE_URL}/auth/login`,
    REGISTER: `${API_BASE_URL}/auth/register`,
    VERIFY_EMAIL: `${API_BASE_URL}/auth/verify-email`,
    RESEND_VERIFICATION: `${API_BASE_URL}/auth/resend-verification`,
    ME: `${API_BASE_URL}/auth/me`,
    FORGOT_PASSWORD: `${API_BASE_URL}/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/auth/reset-password`,
    GOOGLE_CODE_SIGNIN: `${API_BASE_URL}/auth/google/code-signin`,
    REFRESH: `${API_BASE_URL}/auth/refresh`,
    LOGOUT: `${API_BASE_URL}/auth/logout`,
  },
  USERS: {
    BASE: `${API_BASE_URL}/users`,
    PROFILE: `${API_BASE_URL}/users/profile`,
    UPDATE_PROFILE: (userId: string) =>
      `${API_BASE_URL}/users/${userId}/profile`,
    ONBOARDING: `${API_BASE_URL}/users/profile/onboarding`,
    UPDATE_PASSWORD: (userId: string) =>
      `${API_BASE_URL}/users/${userId}/password`,
    ROLES: `${API_BASE_URL}/users/roles`,
    PERMISSIONS: `${API_BASE_URL}/users/permissions`,
    ACTIVITY: (userId: string) => `${API_BASE_URL}/users/${userId}/activity`,
  },
  VISITORS: {
    BASE: `${API_BASE_URL}/visitors`,
    TRACK: `${API_BASE_URL}/visitors/track`,
    JOURNEY: `${API_BASE_URL}/visitors/journey`,
    JOURNEY_BATCH: `${API_BASE_URL}/visitors/journey/batch`,
    UPDATE: (visitorId: string) => `${API_BASE_URL}/visitors/${visitorId}`,
    SESSION: (visitorId: string) =>
      `${API_BASE_URL}/visitors/${visitorId}/session`,
  },
  TOURNAMENTS: {
    BASE: `${API_BASE_URL}/tournaments`,
    MOCK: `${API_BASE_URL}/tournaments/mock`,
    ACTIVE: `${API_BASE_URL}/tournaments/active`,
    ACTIVE_LIST: `${API_BASE_URL}/tournaments/active/list`,
    PUBLIC: `${API_BASE_URL}/tournaments/public`,
    SITEMAP: `${API_BASE_URL}/tournaments/sitemap`,
    PROFILE: (id: string) => `${API_BASE_URL}/tournaments/${id}`,
    START: (id: string) => `${API_BASE_URL}/tournaments/${id}/start`,
    END: (id: string) => `${API_BASE_URL}/tournaments/${id}/end`,
    ENROLLMENT: (id: string) => `${API_BASE_URL}/tournaments/${id}/enrollment`,
    ENROLLMENTS: (id: string) =>
      `${API_BASE_URL}/tournaments/${id}/enrollments`,
    APPROVE_ENROLLMENT: (tournamentId: string, enrollmentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/enrollments/${enrollmentId}/approve`,
    REJECT_ENROLLMENT: (tournamentId: string, enrollmentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/enrollments/${enrollmentId}/reject`,
    GENERATE_PLAYER_REPORTS: (tournamentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/players/generate-pdf`,
    DOWNLOAD_ALL_TEAMS_PLAYERS: (tournamentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/players/download`,
    COPY_PLAYERS: (tournamentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/players/copy`,
    COPY: (tournamentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/copy`,
    REVERT_STATUS: (tournamentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/revert-status`,
    BUDGET_TOPUP: (tournamentId: string) =>
      `${API_BASE_URL}/tournaments/${tournamentId}/budget-topup`,
  },
  TEAMS: {
    BASE: `${API_BASE_URL}/teams`,
    MY_TEAM: `${API_BASE_URL}/teams/my-team`,
    MY_TEAMS: `${API_BASE_URL}/teams/my-teams`,
    SITEMAP: `${API_BASE_URL}/teams/sitemap`,
    PROFILE: (id: string) => `${API_BASE_URL}/teams/${id}`,
    OWNER: (id: string) => `${API_BASE_URL}/teams/${id}/owner`,
    PLAYERS: (teamId: string) => `${API_BASE_URL}/teams/${teamId}/players`,
    DOWNLOAD_PLAYERS: (teamId: string, format: 'pdf' | 'xlsx') =>
      `${API_BASE_URL}/teams/${teamId}/players/download?format=${format}`,
    UPLOAD_LOGO: (teamId: string) => `${API_BASE_URL}/teams/${teamId}/logo`,
  },
  PLAYERS: {
    BASE: `${API_BASE_URL}/players`,
    REEL: (id: string) => `${API_BASE_URL}/players/${id}/reel`,
    REEL_VIDEO: (id: string, template: string) =>
      `${API_BASE_URL}/players/${id}/reel/video?template=${encodeURIComponent(template)}`,
    SITEMAP: `${API_BASE_URL}/players/sitemap`,
    BULK_DELETE: `${API_BASE_URL}/players/bulk-delete`,
    PROFILE: (id: string) => `${API_BASE_URL}/players/${id}`,
    CONTACT: (id: string) => `${API_BASE_URL}/players/${id}/contact`,
    UPLOAD_IMAGE: (id: string) => `${API_BASE_URL}/players/${id}/image`,
    TOURNAMENT: (id: string, searchParams: string) =>
      `${API_BASE_URL}/players/tournament/${id}?${searchParams}`,
    TOURNAMENT_RECENT_ACTIVITY: (id: string, searchParams: string) =>
      `${API_BASE_URL}/players/tournament/${id}/recent-activity?${searchParams}`,
    BULK_UPLOAD: `${API_BASE_URL}/players/bulk-upload`,
    BULK_UPLOAD_STATUS: (importId: string) =>
      `${API_BASE_URL}/players/bulk-upload/${importId}`,
    BULK_TEMPLATE: (tournamentId: string) =>
      `${API_BASE_URL}/players/bulk-template?tournamentId=${tournamentId}`,
    ICON: (id: string) => `${API_BASE_URL}/players/${id}/icon`,
    TEAM: (id: string) => `${API_BASE_URL}/players/${id}/team`,
    REQUEST_ENROLLMENT: `${API_BASE_URL}/players/request-enrollment`,
    ENROLLMENTS: `${API_BASE_URL}/players/enrollments`,
    APPROVE_ENROLLMENT: (enrollmentId: string) =>
      `${API_BASE_URL}/players/enrollment/${enrollmentId}/approve`,
    REJECT_ENROLLMENT: (enrollmentId: string) =>
      `${API_BASE_URL}/players/enrollment/${enrollmentId}/reject`,
    TOURNAMENT_STATS: (tournamentId: string) =>
      `${API_BASE_URL}/players/tournament/${tournamentId}/stats`,
    TOURNAMENT_MOST_CONTESTED: (tournamentId: string) =>
      `${API_BASE_URL}/players/tournament/${tournamentId}/most-contested`,
  },
  AUCTIONS: {
    BASE: `${API_BASE_URL}/auctions`,
    PROFILE: (id: string) => `${API_BASE_URL}/auctions/${id}`,
    START: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/start`,
    BID: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/bid`,
    APPROVE: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/approve`,
    OPT_OUT: (tournamentId: string) =>
      `${API_BASE_URL}/auctions/${tournamentId}/opt-out`,
    PRESALE: `${API_BASE_URL}/auctions/presale`,
    REVERT_PRESALE: `${API_BASE_URL}/auctions/revert-presale`,
    REVERT_BID: `${API_BASE_URL}/auctions/revert-bid`,
    REVERT_SALE: `${API_BASE_URL}/auctions/revert-sale`,
  },
  BIDS: {
    PLAYER_HISTORY: (playerId: string, searchParams: string) =>
      `${API_BASE_URL}/bids/player/${playerId}/history?${searchParams}`,
  },
  POOLS: {
    BASE: `${API_BASE_URL}/pools`,
    PROFILE: (id: string) => `${API_BASE_URL}/pools/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/pools/${id}`,
  },
  ENROLLMENTS: {
    BASE: `${API_BASE_URL}/enrollments`,
    TOURNAMENT: (tournamentId: string) =>
      `${API_BASE_URL}/enrollments/tournament/${tournamentId}`,
  },
  UPLOADS: {
    BASE: `${API_BASE_URL}/uploads`,
  },
  PUBLIC: {},
  SUPPORT: {
    SEND: `${API_BASE_URL}/support`,
  },
  MARKETING: {
    BASE: `${API_BASE_URL}/marketing`,
    UNSUBSCRIBE: `${API_BASE_URL}/marketing/unsubscribe`,
  },
  FEEDBACK: {
    DROPOFF: `${API_BASE_URL}/tournament-feedback/dropoff`,
    COMPLETION: `${API_BASE_URL}/tournament-feedback/completion`,
    COMPLETION_STATUS: (tournamentId: string) =>
      `${API_BASE_URL}/tournament-feedback/completion/${tournamentId}/status`,
  },
  PAYMENTS: {
    PRICING_CONFIG: (countryCode: string, sport?: string) =>
      `${API_BASE_URL}/payments/pricing-config/${countryCode}${
        sport ? `?sport=${encodeURIComponent(sport)}` : ''
      }`,
    PREVIEW: (tournamentId: string) =>
      `${API_BASE_URL}/payments/preview/${tournamentId}`,
    CREATE_ORDER: `${API_BASE_URL}/payments/create-order`,
    VERIFY: `${API_BASE_URL}/payments/verify`,
  },
} as const;

export { API_BASE_URL, WS_BASE_URL };
